import { getPublicDonateGoal } from '../../api/payment/donate-goal.api';
import { getMusicInfo } from '../../api/payment/music-request.api';
import { getPublicAiQuestionSetting } from '../../api/payment/ai-question.api';
import { getPublicLiveEffect } from '../../api/payment/live-effect.api';
import { getPublicDonatePoll } from '../../api/payment/donate-poll.api';
import { getPublicSpinWheel } from '../../api/payment/spin-wheel.api';
import { getPublicMembership } from '../../api/payment/membership.api';

/**
 * Các kiểu donate một streamer có thể bật và cách biết từng kiểu đang bật hay không, theo đúng điều
 * kiện mà khối người xem (creator-*.tsx) dùng để tự hiện. Dùng chung cho dòng nhắc trên trang của
 * chính creator và hàng nhãn "Cách ủng hộ" cạnh nút Donate trên trang streamer.
 */
export type DonateFeatureState = 'on' | 'off' | 'needs-obs';

export interface DonateFeatureStatus {
  key: string;
  label: string;
  muc: string;
  state: DonateFeatureState;
}

const FEATURES: Array<{ key: string; label: string; muc: string; check: (id: string) => Promise<DonateFeatureState> }> = [
  {
    key: 'muc-tieu',
    label: 'Mục tiêu donate',
    muc: 'muc-tieu',
    check: (id) => getPublicDonateGoal(id).then((g) => (g?.exists ? 'on' : 'off')),
  },
  {
    key: 'nhac',
    label: 'Gửi nhạc',
    muc: 'nhac-ai',
    check: (id) => getMusicInfo(id).then((m: any) => (m?.enabled ? 'on' : 'off')),
  },
  {
    key: 'hoi-ai',
    label: 'Hỏi AI',
    muc: 'nhac-ai',
    check: (id) => getPublicAiQuestionSetting(id).then((s: any) => (s?.enabled ? 'on' : 'off')),
  },
  {
    key: 'hieu-ung',
    label: 'Hiệu ứng trên live',
    muc: 'tuong-tac',
    // Khớp với khối người xem: thầu khung tên hiện ngay khi bật; các trò hiệu ứng chỉ bấm được khi
    // lớp phủ OBS đang mở (`available`), bật rồi mà chưa mở OBS thì nhắc mở.
    check: (id) =>
      getPublicLiveEffect(id).then((e) => {
        const hasEffects = !!e?.effects?.length;
        if (e?.billboard?.enabled || (hasEffects && e.available)) return 'on';
        return hasEffects ? 'needs-obs' : 'off';
      }),
  },
  {
    key: 'binh-chon',
    label: 'Bình chọn',
    muc: 'tuong-tac',
    check: (id) => getPublicDonatePoll(id).then((p) => (p?.exists && p.isActive ? 'on' : 'off')),
  },
  {
    key: 'vong-quay',
    label: 'Vòng quay',
    muc: 'tuong-tac',
    check: (id) =>
      getPublicSpinWheel(id).then((w) => (w?.exists && w.isActive && w.items?.length ? 'on' : 'off')),
  },
  {
    key: 'hoi-vien',
    label: 'Hội viên kênh',
    muc: 'hoi-vien',
    check: (id) => getPublicMembership(id).then((m) => (m?.enabled ? 'on' : 'off')),
  },
];

/**
 * Trạng thái từng kiểu donate của một streamer; mục nào đọc lỗi thì bỏ qua, không đoán.
 * `keys` để chỉ hỏi những kiểu cần (vd hàng nhãn trên trang streamer không cần mục tiêu, hội viên).
 */
export const loadDonateFeatureStatus = async (
  userId: string,
  keys?: string[]
): Promise<DonateFeatureStatus[]> => {
  const results = await Promise.all(
    FEATURES.filter((f) => !keys || keys.includes(f.key)).map((f) =>
      f
        .check(userId)
        .then((state) => ({ key: f.key, label: f.label, muc: f.muc, state }))
        .catch(() => null)
    )
  );
  return results.filter((x): x is DonateFeatureStatus => x !== null);
};
